import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
  keyframes,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  People as PeopleIcon,
  LocalShipping as LocalShippingIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  ExpandLess,
  ExpandMore,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Receipt as ReceiptIcon,
  ViewList as ViewListIcon,
  ShoppingBasket as OrdersIcon,
  AssignmentReturn as ReturnsIcon,
  Business as SuppliersIcon,
  Visibility as VisibilityIcon,
  Category as CategoryIcon,
  Straighten as StraightenIcon,
  AttachMoney as AttachMoneyIcon,
  Assessment as AssessmentIcon,
  PointOfSale as PointOfSaleIcon,
  Apps as AppsIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { isAdmin, logout, getCurrentUser } from '../../services/authService';
import notificationService from '../../services/notificationService';
import useNotificationSound from '../../hooks/useNotificationSound';
import { useNotifications } from '../../context/NotificationContext';

// Drawer width when opened
const DRAWER_WIDTH = 240;

// Define the pulse animation keyframes
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

// Define the notification dot animation
const notificationDotAnimation = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

/**
 * Main navigation component with responsive drawer
 * @returns {JSX.Element} Navigation component
 */
const Navigation = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const user = getCurrentUser();
  const { playSound } = useNotificationSound();
  
  // Use centralized notification context instead of local state
  const { notifications, unreadCount, fetchUnreadNotifications } = useNotifications();
  
  // Check if screen is mobile size
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State for drawer, notification menu, user menu, and menu expansions
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [inventoryExpanded, setInventoryExpanded] = useState(false);
  const [salesExpanded, setSalesExpanded] = useState(false);
  const [purchasesExpanded, setPurchasesExpanded] = useState(false);
  const [reportsExpanded, setReportsExpanded] = useState(false);
  const [peopleExpanded, setPeopleExpanded] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  
  // State for notification animations
  const [newNotificationReceived, setNewNotificationReceived] = useState(false);
  const [lastNotificationId, setLastNotificationId] = useState(null);
  
  // Refs
  const lastFetchTimeRef = useRef(Date.now());
  
  // Menu states
  const notificationsMenuOpen = Boolean(notificationsAnchorEl);
  const userMenuOpen = Boolean(userMenuAnchorEl);
  
  // Handle notification animations when unread count changes
  useEffect(() => {
    if (unreadCount > 0) {
      // Trigger animation for new notifications
      setNewNotificationReceived(true);
      setTimeout(() => setNewNotificationReceived(false), 3000);
      
      // Play sound for new notifications
      playSound();
      
      // Set the newest unread notification as the last notification
      const unreadNotifications = notifications.filter(n => !n.read);
      if (unreadNotifications.length > 0) {
        setLastNotificationId(unreadNotifications[0].id);
      }
    }
  }, [unreadCount, notifications, playSound]);
  
  // Menu handlers
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleNotificationsOpen = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
    // Reset animation state
    setNewNotificationReceived(false);
  };
  
  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };
  
  const handleNotificationClick = (notification) => {
    handleNotificationsClose();
    
    // Navigate based on notification type, but always to the main page (not specific item)
    if (notification.type === 'SALES_ORDER') {
      navigate('/sales/view-orders');
    } else if (notification.type === 'PURCHASE_ORDER') {
      navigate('/purchases/view-orders');
    } else if (notification.type === 'LOW_STOCK') {
      navigate('/inventory/stock');
    } else {
      navigate('/notifications');
    }
    
    // Mark notification as read
    if (notification.id && !notification.id.toString().startsWith('low-stock-')) {
      notificationService.markAsRead(notification.id)
        .catch(error => console.error('Error marking notification as read:', error));
    }
  };
  
  // Mark all notifications as read when viewing them
  const handleMarkAllAsRead = useCallback(() => {
    setNewNotificationReceived(false);
    
    // Use the context's markAllAsRead function
    notificationService.markAllAsRead()
      .then(() => {
        // Force a fetch after marking all as read to ensure counter is properly updated
        setTimeout(fetchUnreadNotifications, 500);
      })
      .catch(error => console.error('Error marking all notifications as read:', error));
  }, [fetchUnreadNotifications]);
  
  // Removed automatic mark-as-read on menu open to reduce backend load
  // Now users must manually mark notifications as read
  /*
  useEffect(() => {
    if (notificationsMenuOpen && notifications.some(n => !n.read)) {
      handleMarkAllAsRead();
    }
  }, [notificationsMenuOpen, notifications, handleMarkAllAsRead]);
  */
  
  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };
  
  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };
  
  const handleLogout = () => {
    setUserMenuAnchorEl(null);
    logout(navigate);
  };
  
  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };
  
  // Check if a menu item is active based on the current path
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };
  
  // Toggle submenu expansion
  const handleToggleInventory = () => {
    setInventoryExpanded(!inventoryExpanded);
  };
  
  const handleToggleSales = () => {
    setSalesExpanded(!salesExpanded);
  };
  
  const handleTogglePurchases = () => {
    setPurchasesExpanded(!purchasesExpanded);
  };
  
  const handleToggleReports = () => {
    setReportsExpanded(!reportsExpanded);
  };
  
  const handleTogglePeople = () => {
    setPeopleExpanded(!peopleExpanded);
  };

  const handleToggleSettings = () => {
    setSettingsExpanded(!settingsExpanded);
  };
  
  // Drawer content with navigation items
  const drawerContent = (
    <>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: [1],
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Typography variant="h6" noWrap component="div">
          Tiya Inventory
        </Typography>
        {isMobile && (
          <IconButton 
            onClick={handleDrawerToggle}
            sx={{ color: 'white' }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List component="nav">
        {/* Dashboard */}
        <ListItem disablePadding>
          <ListItemButton 
            selected={isActive('/dashboard')} 
            onClick={() => handleNavigate('/dashboard')}
          >
            <ListItemIcon>
              <DashboardIcon color={isActive('/dashboard') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>
        
        {/* Inventory */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleToggleInventory}>
            <ListItemIcon>
              <InventoryIcon color={isActive('/inventory') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Inventory" />
            {inventoryExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={inventoryExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/inventory/products')}
              onClick={() => handleNavigate('/inventory/products')}
            >
              <ListItemIcon>
                <ViewListIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Products" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/inventory/categories')}
              onClick={() => handleNavigate('/inventory/categories')}
            >
              <ListItemIcon>
                <CategoryIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Categories" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/inventory/units')}
              onClick={() => handleNavigate('/inventory/units')}
            >
              <ListItemIcon>
                <StraightenIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Units of Measurement" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/inventory/taxes')}
              onClick={() => handleNavigate('/inventory/taxes')}
            >
              <ListItemIcon>
                <AttachMoneyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Tax Rates" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/inventory/stock')}
              onClick={() => handleNavigate('/inventory/stock')}
            >
              <ListItemIcon>
                <InventoryIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Stock Control" />
            </ListItemButton>
          </List>
        </Collapse>
        
        {/* Sales */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleToggleSales}>
            <ListItemIcon>
              <ShoppingCartIcon color={isActive('/sales') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Sales" />
            {salesExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={salesExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/sales/pos')}
              onClick={() => handleNavigate('/sales/pos')}
            >
              <ListItemIcon>
                <ReceiptIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Point of Sale" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/sales/view-orders')}
              onClick={() => handleNavigate('/sales/view-orders')}
            >
              <ListItemIcon>
                <ViewListIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="View Orders" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/sales/returns')}
              onClick={() => handleNavigate('/sales/returns')}
            >
              <ListItemIcon>
                <ReturnsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Returns" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/sales/view-returns')}
              onClick={() => handleNavigate('/sales/view-returns')}
            >
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="View Returns" />
            </ListItemButton>
          </List>
        </Collapse>
        
        {/* Purchases */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleTogglePurchases}>
            <ListItemIcon>
              <LocalShippingIcon color={isActive('/purchases') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Purchases" />
            {purchasesExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={purchasesExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/purchases/orders')}
              onClick={() => handleNavigate('/purchases/orders')}
            >
              <ListItemIcon>
                <OrdersIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Orders" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/purchases/view-orders')}
              onClick={() => handleNavigate('/purchases/view-orders')}
            >
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="View Orders" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/purchases/returns')}
              onClick={() => handleNavigate('/purchases/returns')}
            >
              <ListItemIcon>
                <ReturnsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Returns" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/purchases/view-returns')}
              onClick={() => handleNavigate('/purchases/view-returns')}
            >
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="View Returns" />
            </ListItemButton>
          </List>
        </Collapse>
        
        {/* People */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleTogglePeople}>
            <ListItemIcon>
              <PeopleIcon color={isActive('/people') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="People" />
            {peopleExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={peopleExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/sales/customers')}
              onClick={() => handleNavigate('/sales/customers')}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Customers" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/purchases/suppliers')}
              onClick={() => handleNavigate('/purchases/suppliers')}
            >
              <ListItemIcon>
                <SuppliersIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Suppliers" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/people/users')}
              onClick={() => handleNavigate('/people/users')}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Users" />
            </ListItemButton>
          </List>
        </Collapse>
        
        {/* Privileges */}
        {isAdmin() && (
          <>
            <ListItem disablePadding>
              <ListItemButton 
                selected={isActive('/role-privileges')}
                onClick={() => handleNavigate('/role-privileges')}
              >
                <ListItemIcon>
                  <SecurityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Role Privileges" />
              </ListItemButton>
            </ListItem>
          </>
        )}
        
        {/* Reports */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleToggleReports}>
            <ListItemIcon>
              <BarChartIcon color={isActive('/reports') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Reports" />
            {reportsExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={reportsExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>


          <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/reports/products')}
              onClick={() => handleNavigate('/reports/products')}
            >
              <ListItemIcon>
                <ShoppingCartIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Products Reports" />
            </ListItemButton>



            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/reports/sales')}
              onClick={() => handleNavigate('/reports/sales')}
            >
              <ListItemIcon>
                <ShoppingCartIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Sales Reports" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/reports/purchases')}
              onClick={() => handleNavigate('/reports/purchases')}
            >
              <ListItemIcon>
                <LocalShippingIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Purchase Reports" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/inventory/reports')}
              onClick={() => handleNavigate('/inventory/reports')}
            >
              <ListItemIcon>
                <InventoryIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Inventory Reports" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/reports/billing')}
              onClick={() => handleNavigate('/reports/billing')}
            >
              <ListItemIcon>
                <ReceiptIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Billing Reports" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/reports/invoice-profit-loss')}
              onClick={() => handleNavigate('/reports/invoice-profit-loss')}
            >
              <ListItemIcon>
                <AssessmentIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Invoice P&L Reports" />
            </ListItemButton>






            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/reports/credit-customers')}
              onClick={() => handleNavigate('/reports/credit-customers')}
            >
              
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Manage Credit Customers" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Settings */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleToggleSettings}>
            <ListItemIcon>
              <SettingsIcon color={isActive('/settings') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Settings" />
            {settingsExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={settingsExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={isActive('/settings/company')}
              onClick={() => handleNavigate('/settings/company')}
            >
              <ListItemIcon>
                <BusinessIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Company Settings" />
            </ListItemButton>
          </List>
        </Collapse>
      </List>
    </>
  );
  
  return (
    <>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: { sm: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%' },
          ml: { sm: drawerOpen ? `${DRAWER_WIDTH}px` : 0 },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2,
              display: { sm: 'none' }
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1 }}
          >
            Tiya Inventory
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* POS Button */}
            <Tooltip title="Point of Sale">
              <Box
                component="button"
                onClick={() => handleNavigate('/sales/pos')}
                sx={{ 
                  mr: 1,
                  bgcolor: '#00c4b4', 
                  display: 'flex',
                  alignItems: 'center',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  '&:hover': { 
                    bgcolor: '#00a89a' 
                  },
                  boxShadow: '0 0 8px rgba(0,0,0,0.2)',
                }}
              >
                <AppsIcon sx={{ color: 'white', fontSize: 20, mr: 0.75 }} />
                <Typography 
                  variant="button" 
                  component="span"
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                  }}
                >
                  POS
                </Typography>
              </Box>
            </Tooltip>
            
            {/* Notifications */}
            <Tooltip title="Notifications">
              <IconButton
                color="inherit"
                onClick={handleNotificationsOpen}
                size="large"
                sx={{
                  animation: newNotificationReceived ? 
                    `${pulseAnimation} 0.8s ease-in-out infinite` : 'none',
                  position: 'relative',
                  mr: 1
                }}
              >
                                  <Badge 
                    badgeContent={unreadCount} 
                  color="error"
                  showZero={false}
                  sx={{
                    '& .MuiBadge-badge': {
                      animation: newNotificationReceived ? 
                        `${notificationDotAnimation} 0.5s ease-in-out` : 'none',
                      fontSize: '0.6rem',
                      fontWeight: 'bold',
                      minWidth: '18px',
                      height: '18px',
                      padding: '0 4px'
                    }
                  }}
                >
                  <NotificationsIcon sx={{ 
                    fontSize: newNotificationReceived ? 28 : 24,
                    transition: 'all 0.3s ease'
                  }} />
                </Badge>
              </IconButton>
            </Tooltip>
            
            {/* User Profile Menu */}
            <Tooltip title="Account settings">
              <IconButton
                onClick={handleUserMenuOpen}
                size="small"
                sx={{ ml: 2 }}
                aria-controls={userMenuOpen ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={userMenuOpen ? 'true' : undefined}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  {user.name?.charAt(0) || 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Notifications Menu */}
          <Menu
            anchorEl={notificationsAnchorEl}
            open={notificationsMenuOpen}
            onClose={handleNotificationsClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                mt: 1.5,
                width: 350,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1.5,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Notifications</Typography>
              {unreadCount > 0 && (
                <Box 
                  component="button"
                  onClick={handleMarkAllAsRead}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  Mark all as read
                </Box>
              )}
            </Box>
            
            <Divider />
            
            {notifications.length === 0 ? (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ width: '100%' }}>
                  No notifications
                </Typography>
              </MenuItem>
            ) : (
              notifications.slice(0, 5).map((notification, index) => (
                <React.Fragment key={notification.id || index}>
                  <MenuItem 
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      backgroundColor: 'transparent',
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography 
                        variant="subtitle2"
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        {notification.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {notification.message}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          mt: 0.5,
                          display: 'block',
                          textAlign: 'right',
                          fontSize: '0.7rem'
                        }}
                      >
                        {new Date(notification.createdAt).toLocaleString()}
                      </Typography>
                    </Box>
                  </MenuItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
            {notifications.length > 0 && (
              <>
                <Divider />
                <MenuItem onClick={() => {
                  handleNotificationsClose();
                  handleNavigate('/notifications');
                }}>
                  <Typography variant="body2" color="primary" align="center" sx={{ width: '100%' }}>
                    View all notifications
                  </Typography>
                </MenuItem>
              </>
            )}
          </Menu>
          
          {/* User Menu */}
          <Menu
            anchorEl={userMenuAnchorEl}
            id="account-menu"
            open={userMenuOpen}
            onClose={handleUserMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                mt: 1.5,
                minWidth: 200,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleUserMenuClose} disabled sx={{ pointerEvents: 'none' }}>
              <Typography variant="subtitle2">{user.name || 'User'}</Typography>
            </MenuItem>
            <MenuItem onClick={handleUserMenuClose} disabled sx={{ pointerEvents: 'none' }}>
              <Typography variant="body2" color="text.secondary">{user.email || ''}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => {
              handleUserMenuClose();
              handleNavigate('/profile');
            }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      {/* Side Navigation Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={drawerOpen}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            zIndex: theme.zIndex.drawer,
            height: '100%',
            borderRight: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Navigation; 